import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import { useEffect, useRef, useState, useContext } from 'react'
import _isEmpty from 'lodash/isEmpty'
import {
  formatSelectedFiltersWithCounselingType,
  getSelectedFiltersWithCounselingTypeFromQueryParams,
} from 'app/ProviderSearch/utils'
import { PAGINATION_LIMIT } from 'constants/api'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import CounselingFilter from './CounselingFilter'
import AppointmentModeFilter from './AppointmentModeFilter'
import { modifyURLQuery } from 'utils/common'
import { MobileFilterModalContext } from 'context/ProviderSearch/MobileFilterModalProvider'
import { useAllFilters } from 'context/ProviderSearch/AllFiltersProvider'
import PillMultiselectDropdownFilter from './PillMultiselectDropdownFilter'
import { RETURN_FILTER_KEY_MAPPING } from 'app/ProviderSearch/constants'
import SortFilter from 'app/ProviderSearch/ProviderFilter/SortFilter'
import useBreakpoint from 'hooks/useBreakPoint'
import filterModalStyles from 'app/ProviderSearch/MobileFilterModal/index.module.scss'
import useThriveAuth from 'hooks/useThriveAuth'
import { If } from '@/utility_components/ControlStatements'

const MobileFilterModal = dynamic(
  () => import('app/ProviderSearch/MobileFilterModal')
)

type ProviderFilterProps = {
  groupedFilters: GroupedProviderFilter
  // Show the sort filter based on this.
  isInPersonSelected: boolean
  isExtendedBookingEnabled?: boolean
}

const ProviderFilter: React.FC<ProviderFilterProps> = ({
  groupedFilters,
  isInPersonSelected,
  isExtendedBookingEnabled = false,
}) => {
  const router = useRouter()
  const { query } = router
  const initialMount = useRef(true)
  const filterUpdate = useRef(true)
  const { isSupportOrThirdPartyUser } = useThriveAuth()
  const allGroupedFilters = useAllFilters()

  const {
    showMobileFilterModal,
    setShowMobileFilterModal,
    clearFilterToExpand,
    filterToExpand,
  } = useContext(MobileFilterModalContext)
  const { isMobileScreen, isLargeSize } = useBreakpoint()
  const [selectedFilter, setSelectedFilter] = useState<GroupedProviderFilter>(
    {}
  )

  useEffect(() => {
    // updates the selected filter from the query only once when the component is mounted and query param is present
    // or if there is a query update without performing remove or apply action

    // Resolves selections the state doesn't offer from the full filter list,
    // so the mobile panel keeps them
    const selectedFilterFromQuery = getSelectedFiltersWithCounselingTypeFromQueryParams(
      groupedFilters,
      query as NodeJS.Dict<string[]>,
      allGroupedFilters
    )

    if (!_isEmpty(query) && (initialMount.current || filterUpdate.current)) {
      setSelectedFilter(selectedFilterFromQuery)
      initialMount.current = false
    } else if (!_isEmpty(selectedFilter) && _isEmpty(selectedFilterFromQuery)) {
      setSelectedFilter({})
    }

    filterUpdate.current = true
  }, [query])

  useEffect(() => {
    if (!(isMobileScreen || isLargeSize) && showMobileFilterModal) {
      setShowMobileFilterModal(false)
    }
  }, [isMobileScreen, isLargeSize, showMobileFilterModal])

  const applyFilters = (
    groupedFilters: GroupedProviderFilter,
    additionalFilter?: Record<string, unknown>,
    removeExistingFilters?: boolean
  ) => {
    filterUpdate.current = false
    let formattedFilters = formatSelectedFiltersWithCounselingType(
      groupedFilters
    )
    const { partnershipCode } = query ?? {}

    if (additionalFilter) {
      formattedFilters = {
        ...formattedFilters,
        ...additionalFilter,
      }
    }

    modifyURLQuery(
      router,
      {
        ...formattedFilters,
        limit: PAGINATION_LIMIT.toString(),
        offset: '0',
        ...(!!partnershipCode && { partnershipCode }),
      },
      !!removeExistingFilters
    )
  }

  const onHide = () => {
    // Add custom class for slide-down animation
    document
      .querySelector('.modal-content')
      .classList.add(filterModalStyles.modal_slidedown)

    clearFilterToExpand()

    setTimeout(() => {
      setShowMobileFilterModal(false)
    }, 200)
  }

  return (
    <>
      <CounselingFilter
        allCounselingTypes={groupedFilters[ProviderFilterTypes.COUNSELING_TYPE]}
      />

      <AppointmentModeFilter
        allAppointmentModes={
          groupedFilters[ProviderFilterTypes.AVAILABILITY_TYPE]
        }
      />
      <If
        condition={Boolean(
          groupedFilters[ProviderFilterTypes.DAY_OF_WEEK]?.length
        )}
      >
        <PillMultiselectDropdownFilter
          filterList={groupedFilters[ProviderFilterTypes.DAY_OF_WEEK]}
          filterType={ProviderFilterTypes.DAY_OF_WEEK}
          filterKey={RETURN_FILTER_KEY_MAPPING.day_of_week}
          placeholder="Day of the week"
          filterTypeText="day of the week"
          showListHeaders={false}
          hideFilterSearchInput
          useCompactDropdownPadding
        />
      </If>
      <PillMultiselectDropdownFilter
        filterList={groupedFilters[ProviderFilterTypes.TIME_OF_DAY]}
        filterType={ProviderFilterTypes.TIME_OF_DAY}
        filterKey={RETURN_FILTER_KEY_MAPPING.time_of_day}
        placeholder="Time of day"
        filterTypeText="time of day"
        showListHeaders={false}
      />
      <PillMultiselectDropdownFilter
        filterList={groupedFilters[ProviderFilterTypes.GENDER]}
        filterType={ProviderFilterTypes.GENDER}
        filterKey={RETURN_FILTER_KEY_MAPPING.gender}
        placeholder="Provider gender"
        filterTypeText="provider genders"
        showListHeaders={false}
      />
      <PillMultiselectDropdownFilter
        filterList={groupedFilters[ProviderFilterTypes.CLIENT_AGE_GROUP]}
        filterType={ProviderFilterTypes.CLIENT_AGE_GROUP}
        filterKey={RETURN_FILTER_KEY_MAPPING.client_age_group}
        placeholder="Client age"
        filterTypeText="client ages"
        showListHeaders={false}
        hideFilterSearchInput
      />
      <PillMultiselectDropdownFilter
        filterList={groupedFilters[ProviderFilterTypes.LANGUAGE]}
        filterType={ProviderFilterTypes.LANGUAGE}
        filterKey={RETURN_FILTER_KEY_MAPPING.language}
        placeholder="Language"
        filterTypeText="languages"
      />
      <PillMultiselectDropdownFilter
        filterList={groupedFilters[ProviderFilterTypes.COUNSELING_APPROACH]}
        filterType={ProviderFilterTypes.COUNSELING_APPROACH}
        filterKey={RETURN_FILTER_KEY_MAPPING.counseling_approach}
        placeholder="Therapy approach"
        filterTypeText="therapy approaches"
      />

      <If condition={isInPersonSelected || isSupportOrThirdPartyUser}>
        <SortFilter />
      </If>

      <If condition={isMobileScreen || isLargeSize}>
        <MobileFilterModal
          showFilterModal={showMobileFilterModal}
          isInPersonSelected={isInPersonSelected}
          isExtendedBookingEnabled={isExtendedBookingEnabled}
          filterToExpand={filterToExpand}
          selectedFilter={selectedFilter}
          setSelectedFilter={(
            groupedFilters: GroupedProviderFilter,
            additionalFilter?: Record<string, unknown>
          ) => {
            applyFilters(groupedFilters, additionalFilter, true)
            setSelectedFilter(groupedFilters)
          }}
          groupedFilters={groupedFilters}
          onHide={onHide}
        />
      </If>
    </>
  )
}

export default ProviderFilter
